import { ethers } from 'ethers';
import { walletIcons } from '../assets/walletIcons.js';

// EIP-6963: Bidirectional mapping between RDNS identifiers and legacy wallet names
const RDNS_TO_LEGACY = {
    'io.metamask': 'metamask',
    'io.rabby': 'rabby',
    'me.rainbow': 'rainbow',
    'app.phantom': 'phantom',
    'com.coinbase.wallet': 'coinbase',
    'com.trustwallet.app': 'trustwallet',
    'com.okex.wallet': 'okx',
};
const LEGACY_TO_RDNS = Object.fromEntries(
    Object.entries(RDNS_TO_LEGACY).map(([rdns, legacy]) => [legacy, rdns])
);

/**
 * WalletService - Handles wallet connection, detection, and interactions
 */
class WalletService {
    constructor(eventBus) {
        if (!eventBus) {
            throw new Error('WalletService requires an eventBus instance.');
        }
        this.eventBus = eventBus;
        this.provider = null;
        this.signer = null;
        this.connectedAddress = null;
        this.selectedWallet = null;
        this.connected = false;
        this.ethers = ethers;
        this.isInitialized = false;

        // EIP-6963 discovered providers: uuid -> { info, provider }
        this.eip6963Providers = new Map();
        this._eip6963Handler = null;

        // Supported wallet providers - using a more careful detection approach
        this.providerMap = {
            rabby: () => {
                if (window.ethereum && window.ethereum.isRabby) {
                    return window.ethereum;
                }
                return null;
            },
            rainbow: () => {
                if (window.ethereum && window.ethereum.isRainbow) {
                    return window.ethereum;
                }
                return window.rainbow || null;
            },
            phantom: () => {
                if (window.phantom && window.phantom.ethereum) {
                    return window.phantom.ethereum;
                }
                return null;
            },
            metamask: () => {
                console.log('Checking for MetaMask with direct window.ethereum access');
                return window.ethereum || null;
            }
        };

        // Wallet icons mapping (inline SVGs)
        this.walletIcons = walletIcons;

        // We'll set up event listeners after a wallet is selected, not in constructor
    }
    
    /**
     * Start EIP-6963 provider discovery.
     * Listens for wallet announcements and dispatches a request for wallets to announce themselves.
     * @private
     */
    _startEIP6963Discovery() {
        if (this._eip6963Handler) return;

        this._eip6963Handler = (event) => {
            const { info, provider } = event.detail;
            if (!info || !provider) return;

            console.log('[WalletService] EIP-6963 provider discovered:', info.name, info.rdns);
            this.eip6963Providers.set(info.uuid, { info, provider });
            this.eventBus.emit('wallet:providerdiscovered', { info, provider });
        };

        window.addEventListener('eip6963:announceProvider', this._eip6963Handler);
        window.dispatchEvent(new Event('eip6963:requestProvider'));
    }

    /**
     * Stop listening for EIP-6963 announcements.
     * @private
     */
    _stopEIP6963Discovery() {
        if (this._eip6963Handler) {
            window.removeEventListener('eip6963:announceProvider', this._eip6963Handler);
            this._eip6963Handler = null;
        }
    }

    /**
     * Find an EIP-6963 provider by rdns, uuid, or legacy name.
     * @private
     * @param {string} identifier - rdns, uuid, or legacy wallet name
     * @returns {{ info: Object, provider: Object } | null}
     */
    _findEIP6963Provider(identifier) {
        if (!identifier) return null;

        // Direct uuid lookup
        if (this.eip6963Providers.has(identifier)) {
            return this.eip6963Providers.get(identifier);
        }

        // Convert legacy name to rdns if needed
        const rdns = LEGACY_TO_RDNS[identifier] || identifier;

        for (const entry of this.eip6963Providers.values()) {
            if (entry.info.rdns === rdns) return entry;
        }

        return null;
    }

    /**
     * Destroy the service: cleanup connections, stop discovery, clear providers.
     */
    destroy() {
        this.cleanup();
        this._stopEIP6963Discovery();
        this.eip6963Providers.clear();
    }

    /**
     * Initialize the wallet service.
     * Automatically reconnects to previously authorized wallets by default.
     * @param {Object} options - Initialization options
     * @param {boolean} [options.autoReconnect=true] - Set to false to disable auto-reconnect
     * @returns {Promise<boolean>} True if initialized successfully
     */
    async initialize(options = {}) {
        const { autoReconnect = true, lateDetectionWindow = 3000 } = options;

        try {
            console.log('Initializing WalletService...');

            // Start EIP-6963 discovery and wait briefly for announcements
            this._startEIP6963Discovery();

            // Listen for ethereum#initialized — fired by mobile injectors (ethOS, Trust Wallet mobile)
            // that set window.ethereum slightly after page load. We remove it after lateDetectionWindow ms.
            const ethereumInitHandler = () => {
                if (typeof window.ethereum !== 'undefined' && !this.connected) {
                    this.eventBus.emit('wallet:lateProviderDetected', { provider: window.ethereum });
                }
            };
            window.addEventListener('ethereum#initialized', ethereumInitHandler, { once: true });
            setTimeout(() => window.removeEventListener('ethereum#initialized', ethereumInitHandler), lateDetectionWindow);

            await new Promise(resolve => setTimeout(resolve, 100));
            this.eventBus.emit('wallet:providerlistready', { providers: this.eip6963Providers });

            const hasEIP6963 = this.eip6963Providers.size > 0;
            const hasLegacy = typeof window.ethereum !== 'undefined';

            if (hasEIP6963 || hasLegacy) {
                console.log(`Found providers: ${this.eip6963Providers.size} EIP-6963, legacy=${hasLegacy}`);
                this.eventBus.emit('wallet:detected');
            }

            if (hasLegacy) {
                this.isMetaMask = window.ethereum.isMetaMask;
            }

            // Auto-reconnect: try EIP-6963 first, then legacy
            if (autoReconnect) {
                const lastWallet = localStorage.getItem('ms2fun_lastWallet');
                let reconnected = false;

                // Try EIP-6963 auto-reconnect
                if (lastWallet && hasEIP6963) {
                    const eip6963Match = this._findEIP6963Provider(lastWallet);
                    if (eip6963Match) {
                        try {
                            const accounts = await eip6963Match.provider.request({ method: 'eth_accounts' });
                            if (accounts?.length > 0) {
                                this.selectedWallet = eip6963Match.info.rdns;
                                this.provider = eip6963Match.provider;
                                this.connectedAddress = accounts[0];
                                this.connected = true;

                                this.ethersProvider = new ethers.providers.Web3Provider(eip6963Match.provider, 'any');
                                this.signer = this.ethersProvider.getSigner();
                                this.setupEventListeners();

                                // Upgrade localStorage to rdns
                                localStorage.setItem('ms2fun_lastWallet', eip6963Match.info.rdns);

                                console.log('[WalletService] EIP-6963 auto-connected to:', accounts[0]);
                                this.eventBus.emit('wallet:connected', {
                                    address: this.connectedAddress,
                                    walletType: this.selectedWallet,
                                    provider: this.provider,
                                    ethersProvider: this.ethersProvider,
                                    signer: this.signer
                                });
                                reconnected = true;
                            }
                        } catch (err) {
                            console.warn('[WalletService] EIP-6963 auto-reconnect failed:', err.message);
                        }
                    }
                }

                // Fall back to legacy auto-reconnect
                if (!reconnected && hasLegacy) {
                    const accounts = await window.ethereum.request({ method: 'eth_accounts' });
                    if (accounts?.length > 0) {
                        console.log('[WalletService] Found authorized account:', accounts[0]);

                        const walletType = this._detectWalletType();
                        this.selectedWallet = walletType;
                        this.provider = window.ethereum;
                        this.connectedAddress = accounts[0];
                        this.connected = true;

                        this.ethersProvider = new ethers.providers.Web3Provider(window.ethereum, 'any');
                        this.signer = this.ethersProvider.getSigner();
                        this.setupEventListeners();

                        localStorage.setItem('ms2fun_lastWallet', walletType);
                        console.log('[WalletService] Auto-connected to:', accounts[0]);

                        this.eventBus.emit('wallet:connected', {
                            address: this.connectedAddress,
                            walletType: this.selectedWallet,
                            provider: this.provider,
                            ethersProvider: this.ethersProvider,
                            signer: this.signer
                        });
                    }
                }
            }

            if (!hasEIP6963 && !hasLegacy) {
                console.log('No Ethereum provider found');
                this.eventBus.emit('wallet:notdetected');
            }

            // Set up listeners even if not connected (for future connections)
            if (!this.connected) {
                this.setupEventListeners();
            }

            this.isInitialized = true;
            return true;
        } catch (error) {
            console.error('Error initializing WalletService:', error);
            this.isInitialized = true; // Still mark as initialized
            throw error;
        }
    }

    /**
     * Attempt to auto-connect to a previously authorized wallet.
     * Uses eth_accounts (non-prompting) to check for existing authorization.
     * @returns {Promise<string|null>} The connected address, or null if no authorized wallet found
     */
    async tryAutoConnect() {
        try {
            if (typeof window.ethereum === 'undefined') {
                return null;
            }

            // eth_accounts returns already-authorized accounts without prompting
            const accounts = await window.ethereum.request({ method: 'eth_accounts' });

            if (!accounts || accounts.length === 0) {
                console.log('[WalletService] No previously authorized accounts found');
                return null;
            }

            console.log('[WalletService] Found authorized account:', accounts[0]);

            // Detect which wallet type is active
            const walletType = this._detectWalletType();
            console.log('[WalletService] Detected wallet type:', walletType);

            // Select and connect (this won't prompt since already authorized)
            await this.selectWallet(walletType);
            const address = await this.connect();

            console.log('[WalletService] Auto-reconnected to:', address);
            return address;

        } catch (error) {
            console.warn('[WalletService] Auto-connect failed:', error.message);
            return null;
        }
    }

    /**
     * Detect which wallet type is currently active based on provider flags
     * @private
     * @returns {string} The detected wallet type
     */
    _detectWalletType() {
        // If selectedWallet already matches an EIP-6963 provider, return its rdns
        if (this.selectedWallet && this._findEIP6963Provider(this.selectedWallet)) {
            const match = this._findEIP6963Provider(this.selectedWallet);
            return match.info.rdns;
        }

        // Check localStorage and try EIP-6963 match
        const lastWallet = localStorage.getItem('ms2fun_lastWallet');
        if (lastWallet) {
            const eip6963Match = this._findEIP6963Provider(lastWallet);
            if (eip6963Match) return eip6963Match.info.rdns;
            if (this.providerMap[lastWallet]) return lastWallet;
        }

        if (typeof window.ethereum === 'undefined') return 'metamask';

        // Legacy flag detection fallback
        if (window.ethereum.isRabby) return 'rabby';
        if (window.ethereum.isRainbow) return 'rainbow';
        if (window.phantom?.ethereum) return 'phantom';

        // Default to metamask for any EIP-1193 provider
        return 'metamask';
    }
    
    /**
     * Format error to provide better context
     * @private
     */
    formatError(error, context = '') {
        // If error is a string, convert to Error object
        if (typeof error === 'string') {
            return new Error(`${context}: ${error}`);
        }
        
        // If error has a message, add context
        if (error && error.message) {
            // Common wallet errors, make more user-friendly
            const message = this.getUserFriendlyErrorMessage(error, context);
            const formattedError = new Error(message);
            
            // Copy properties
            formattedError.code = error.code;
            formattedError.originalError = error;
            
            return formattedError;
        }
        
        // Default case
        return new Error(`${context}: Unknown error`);
    }
    
    /**
     * Convert technical errors to user-friendly messages
     * @private
     */
    getUserFriendlyErrorMessage(error, context) {
        const message = error.message || 'Unknown error';
        
        // Handle common wallet error codes
        if (error.code === 4001) {
            return 'Connection request rejected by user';
        }
        
        if (error.code === 4902) {
            return 'Network needs to be added to your wallet';
        }
        
        if (message.includes('redefine property') || message.includes('which has only a getter')) {
            return 'Wallet conflict detected. Please disable all wallet extensions except the one you want to use, then refresh.';
        }
        
        if (message.includes('user rejected')) {
            return 'Connection rejected by user';
        }
        
        if (message.includes('already pending')) {
            return 'A wallet request is already pending, please check your wallet';
        }
        
        // If no specific handling, add context
        return context ? `${context}: ${message}` : message;
    }
    
    /**
     * Get all available wallet providers.
     * Returns uniform wallet objects combining EIP-6963 and legacy providers.
     * EIP-6963 wallets appear first; legacy wallets are only added if not already
     * discovered via EIP-6963 (deduplication via RDNS mapping).
     * @returns {Object} Map of wallet identifier -> { name, icon, provider, rdns, uuid, legacyName, source }
     */
    getAvailableWallets() {
        const available = {};
        const discoveredRdns = new Set();

        console.log('Checking for available wallets...');

        // 1. Add EIP-6963 wallets first
        for (const [uuid, { info, provider }] of this.eip6963Providers) {
            const key = info.rdns || uuid;
            const legacyName = RDNS_TO_LEGACY[info.rdns] || null;
            console.log(`Found EIP-6963 wallet: ${info.name} (${info.rdns})`);
            available[key] = {
                name: info.name,
                icon: info.icon || (legacyName && this.walletIcons[legacyName]) || null,
                provider,
                rdns: info.rdns,
                uuid,
                legacyName,
                source: 'eip6963'
            };
            if (info.rdns) discoveredRdns.add(info.rdns);
        }

        // 2. Add legacy providerMap wallets not already discovered via EIP-6963
        for (const [name, getProvider] of Object.entries(this.providerMap)) {
            try {
                const rdns = LEGACY_TO_RDNS[name];
                if (rdns && discoveredRdns.has(rdns)) {
                    console.log(`Skipping legacy ${name} - already discovered via EIP-6963`);
                    continue;
                }
                const provider = getProvider();
                if (provider) {
                    console.log(`Found legacy wallet: ${name}`);
                    available[name] = {
                        name: name.charAt(0).toUpperCase() + name.slice(1),
                        icon: this.walletIcons[name] || null,
                        provider,
                        rdns: rdns || null,
                        uuid: null,
                        legacyName: name,
                        source: 'legacy'
                    };
                }
            } catch (error) {
                console.warn(`Error detecting ${name} wallet:`, error);
            }
        }

        // 3. MetaMask fallback for bare window.ethereum
        if (!available['metamask'] && !discoveredRdns.has('io.metamask') && window.ethereum) {
            console.log('Adding MetaMask fallback for bare window.ethereum');
            available['metamask'] = {
                name: 'MetaMask',
                icon: this.walletIcons['metamask'] || null,
                provider: window.ethereum,
                rdns: 'io.metamask',
                uuid: null,
                legacyName: 'metamask',
                source: 'legacy'
            };
        }

        console.log('Available wallets:', Object.keys(available));
        return available;
    }
    
    /**
     * Select a specific wallet provider.
     * Tries EIP-6963 lookup first, then falls back to legacy providerMap.
     * @param {string} walletType - rdns, uuid, or legacy wallet name
     */
    async selectWallet(walletType) {
        try {
            // Clear any previous wallet selection
            this.cleanup();

            // Try EIP-6963 first
            const eip6963Match = this._findEIP6963Provider(walletType);
            if (eip6963Match) {
                this.selectedWallet = eip6963Match.info.rdns;
                this.provider = eip6963Match.provider;
                this.setupEventListeners();

                this.eventBus.emit('wallet:selected', {
                    type: eip6963Match.info.rdns,
                    provider: eip6963Match.provider,
                    source: 'eip6963'
                });
                return eip6963Match.info.rdns;
            }

            // Fall back to legacy providerMap
            if (!this.providerMap[walletType]) {
                throw new Error(`Unsupported wallet: ${walletType}`);
            }

            const provider = this.providerMap[walletType]();
            if (!provider) {
                throw new Error(`${walletType} not detected. Please install it first.`);
            }

            this.selectedWallet = walletType;
            this.provider = provider;
            this.setupEventListeners();

            this.eventBus.emit('wallet:selected', {
                type: walletType,
                provider: provider,
                source: 'legacy'
            });

            return walletType;
        } catch (error) {
            const formattedError = this.formatError(error, 'Wallet selection failed');
            this.eventBus.emit('wallet:error', formattedError);
            throw formattedError;
        }
    }
    
    /**
     * Set up event listeners for the selected wallet
     * @private
     */
    setupEventListeners() {
        if (this.provider) {
            // Remove any existing listeners to avoid duplicates
            try {
                this.provider.removeListener('chainChanged', this.handleNetworkChange);
                this.provider.removeListener('accountsChanged', this.handleAccountChange);
            } catch (error) {
                // Ignore errors when removing non-existent listeners
            }
            
            // Add new listeners
            this.provider.on('chainChanged', () => this.handleNetworkChange());
            this.provider.on('accountsChanged', (accounts) => this.handleAccountChange(accounts));
        }
    }
    
    /**
     * Clean up resources when switching wallets
     * @private
     */
    cleanup() {
        if (this.provider) {
            try {
                this.provider.removeListener('chainChanged', this.handleNetworkChange);
                this.provider.removeListener('accountsChanged', this.handleAccountChange);
            } catch (error) {
                // Ignore errors when removing non-existent listeners
            }
        }
        
        this.provider = null;
        this.signer = null;
        this.connectedAddress = null;
        this.connected = false;
        this.ethersProvider = null;
    }
    
    /**
     * Connect to a wallet
     * @param {string} [walletType] - Optional wallet type to connect to directly (e.g., 'metamask', 'rabby')
     *                                If not provided, requires prior call to selectWallet()
     * @returns {Promise<string>} The connected address
     */
    async connect(walletType) {
        try {
            // If walletType provided, select it first
            if (walletType) {
                await this.selectWallet(walletType);
            }

            if (!this.selectedWallet || !this.provider) {
                throw new Error('Please select a wallet first');
            }
            
            this.eventBus.emit('wallet:connecting');
            console.log(`Attempting to connect to ${this.selectedWallet}...`);
            
            let accounts;
            
            try {
                // Give the wallet time to initialize if needed
                await new Promise(resolve => setTimeout(resolve, 500));
                
                // Handle wallet-specific connection methods
                // Only use Phantom's special path if NOT discovered via EIP-6963
                const phantomViaEIP6963 = this._findEIP6963Provider('app.phantom');
                if (this.selectedWallet === 'phantom' && !phantomViaEIP6963 && window.phantom && window.phantom.ethereum) {
                    console.log('Using phantom-specific connection method (legacy)');
                    accounts = await window.phantom.ethereum.request({
                        method: 'eth_requestAccounts'
                    });
                } else {
                    console.log(`Using standard connection method for ${this.selectedWallet}`);
                    // Try to check if the wallet has any accounts before requesting new ones
                    try {
                        const existingAccounts = await this.provider.request({
                            method: 'eth_accounts'
                        });
                        
                        console.log('Existing accounts:', existingAccounts);
                        
                        // If no accounts, explicitly check before requesting
                        if (!existingAccounts || existingAccounts.length === 0) {
                            console.log('No existing accounts found, requesting user approval...');
                        }
                    } catch (accountCheckError) {
                        console.warn('Error checking existing accounts:', accountCheckError);
                    }
                    
                    // Request accounts with explicit params
                    accounts = await this.provider.request({
                        method: 'eth_requestAccounts',
                        params: []
                    });
                }
            } catch (error) {
                console.error('Error requesting accounts:', error);
                
                if (error && error.code === 4001) {
                    throw this.formatError(error, 'Wallet connection rejected');
                } else if (error && error.message && error.message.includes('wallet must has at least one account')) {
                    throw new Error('Your wallet has no accounts. Please create at least one account in your wallet and try again.');
                } else {
                    throw error;
                }
            }
            
            console.log('Accounts received:', accounts);
            
            if (!accounts || !accounts[0]) {
                throw new Error('No accounts found. Please unlock your wallet and make sure you have at least one account set up.');
            }
            
            this.connectedAddress = accounts[0];
            this.connected = true;
            
            console.log(`Successfully connected to account: ${this.connectedAddress}`);
            
            // Store the selected wallet in localStorage for future auto-reconnect
            if (this.selectedWallet) {
                localStorage.setItem('ms2fun_lastWallet', this.selectedWallet);
            }
            
            // Create ethers provider
            this.ethersProvider = new ethers.providers.Web3Provider(this.provider, 'any');
            this.signer = this.ethersProvider.getSigner();
            
            this.eventBus.emit('wallet:connected', {
                address: this.connectedAddress,
                walletType: this.selectedWallet,
                provider: this.provider,
                ethersProvider: this.ethersProvider,
                signer: this.signer
            });
            
            return this.connectedAddress;
        } catch (error) {
            console.error('Wallet connection error:', error);
            
            // Better handle the specific "must have at least one account" error
            if (error.message && (
                error.message.includes('at least one account') || 
                error.message.includes('wallet must has') ||
                error.message.includes('no accounts')
            )) {
                const friendlyMessage = 'Your wallet has no accounts. Please create at least one account in your wallet and try again.';
                const formattedError = new Error(friendlyMessage);
                formattedError.code = error.code;
                formattedError.originalError = error;
                
                this.eventBus.emit('wallet:error', formattedError);
                throw formattedError;
            }
            
            const formattedError = this.formatError(error, 'Wallet connection failed');
            this.eventBus.emit('wallet:error', formattedError);
            throw formattedError;
        }
    }
    
    /**
     * Switch to a specific network
     * @param {number} networkId - The network ID to switch to
     */
    async switchNetwork(networkId) {
        try {
            if (!this.provider) {
                throw new Error('No wallet connected');
            }
            
            const hexChainId = `0x${Number(networkId).toString(16)}`;
            
            this.eventBus.emit('network:switching', {
                to: networkId,
                automatic: false
            });
            
            try {
                await this.provider.request({
                    method: 'wallet_switchEthereumChain',
                    params: [{ chainId: hexChainId }],
                });
                
                // Refresh provider after switch
                this.ethersProvider = new ethers.providers.Web3Provider(this.provider, 'any');
                this.signer = this.ethersProvider.getSigner();
                
                this.eventBus.emit('network:switched', {
                    to: networkId,
                    success: true
                });
                
                return true;
            } catch (switchError) {
                // Network needs to be added
                if (switchError.code === 4902) {
                    // You would need network metadata here to add properly
                    throw new Error('Network needs to be added first');
                } else {
                    throw switchError;
                }
            }
        } catch (error) {
            const formattedError = this.formatError(error, 'Network switch failed');
            
            // Emit error event for consistency with BlockchainService
            this.eventBus.emit('network:switch:error', {
                from: null,
                to: networkId,
                error: formattedError.message,
                originalError: error
            });
            
            // Also emit switched event with success: false for backward compatibility
            this.eventBus.emit('network:switched', {
                to: networkId,
                success: false,
                error: formattedError.message
            });
            
            throw formattedError;
        }
    }
    
    /**
     * Get the currently connected address
     * @returns {string|null} The connected address or null
     */
    getAddress() {
        return this.connectedAddress;
    }
    
    /**
     * Check if a wallet is connected
     * @returns {boolean} Whether a wallet is connected
     */
    isConnected() {
        return this.connected && !!this.connectedAddress;
    }
    
    /**
     * Get the ethers provider and signer
     * @returns {Object} The provider and signer
     */
    getProviderAndSigner() {
        return {
            provider: this.ethersProvider,
            signer: this.signer
        };
    }

    /**
     * Programmatically set the connected state.
     * Useful for migrating from manual workarounds or external connection management.
     *
     * @param {Object} state - The connection state to set
     * @param {string} state.address - The connected wallet address
     * @param {Object} state.provider - The raw provider (window.ethereum or similar)
     * @param {Object} [state.ethersProvider] - Optional ethers Web3Provider (created if not provided)
     * @param {Object} [state.signer] - Optional ethers signer (created if not provided)
     * @param {string} [state.walletType='metamask'] - The wallet type identifier
     * @param {boolean} [emitEvent=true] - Whether to emit wallet:connected event
     * @returns {void}
     */
    setConnectedState({ address, provider, ethersProvider, signer, walletType = 'metamask' }, emitEvent = true) {
        if (!address) {
            throw new Error('setConnectedState requires an address');
        }
        if (!provider) {
            throw new Error('setConnectedState requires a provider');
        }

        // Update internal state
        this.provider = provider;
        this.connectedAddress = address;
        this.connected = true;
        this.selectedWallet = walletType;

        // Create ethers provider/signer if not provided
        this.ethersProvider = ethersProvider || new ethers.providers.Web3Provider(provider, 'any');
        this.signer = signer || this.ethersProvider.getSigner();

        // Set up event listeners for the provider
        this.setupEventListeners();

        // Store wallet type for future auto-reconnect
        localStorage.setItem('ms2fun_lastWallet', walletType);

        console.log('[WalletService] State set programmatically:', address);

        // Emit event so UI components update
        if (emitEvent) {
            this.eventBus.emit('wallet:connected', {
                address: this.connectedAddress,
                walletType: this.selectedWallet,
                provider: this.provider,
                ethersProvider: this.ethersProvider,
                signer: this.signer
            });
        }
    }

    /**
     * Handle network changes
     * @private
     */
    handleNetworkChange() {
        // Refresh provider
        if (this.provider) {
            this.ethersProvider = new ethers.providers.Web3Provider(this.provider, 'any');
            this.signer = this.ethersProvider.getSigner();
            
            // Don't emit network:changed here - BlockchainService handles it
            // This prevents duplicate messages when both services detect the change
        }
    }
    
    /**
     * Handle account changes
     * @private
     */
    handleAccountChange(accounts) {
        if (!accounts || !Array.isArray(accounts)) {
            return;
        }
        
        if (accounts.length === 0) {
            // Disconnected
            this.connected = false;
            this.connectedAddress = null;
            this.eventBus.emit('wallet:disconnected');
        } else if (accounts[0] !== this.connectedAddress) {
            // Changed account
            this.connectedAddress = accounts[0];
            this.eventBus.emit('wallet:changed', { address: accounts[0] });
        }
    }
    
    /**
     * Disconnect from the wallet (if supported)
     */
    async disconnect() {
        // Most wallets don't support programmatic disconnect
        // But we can reset our state
        this.cleanup();
        this.eventBus.emit('wallet:disconnected');
    }
}

export { RDNS_TO_LEGACY, LEGACY_TO_RDNS };
export default WalletService;
 